/**
 * Realtor reach: which part of a Vitec office one agent may see.
 *
 * Vitec issues one key per office, so its API answers every question about
 * every realtor's listings. An agent working for one realtor at a firm with
 * several must not read the others' listings, bids or commission, and Vitec
 * cannot enforce that for us (no per-realtor key; `User/AuthenticateUser`
 * takes the realtor's own password, which we must not hold). The limit is
 * ours: the rules per operation are in `reach-rules.ts`, the gate in the
 * runtime client.
 *
 * Reach is the WHOLE OFFICE, or a SET OF REALTORS (Express user ids). The two
 * are different on purpose (Erik, 2026-10-14): the whole office includes
 * realtors who join later and the office-wide figures; a set is exactly the
 * realtors named. A listing belongs to the realtor Vitec names as
 * responsible for it (`assignment.responsibleBroker`), never to co-brokers.
 *
 * The customer REQUESTS a reach per agent (connection metadata,
 * `requested_reach`); a Squidler operator APPROVES it, or a narrower one
 * (`integration_connections.approved_reach`, V485, which the customer cannot
 * write). The agent gets the narrower of the two — V482's consent pattern on
 * a second axis. With no approval yet, the agent has no listing data at all.
 *
 * Contacts are the office's (Erik): a limited reach does not filter them.
 */

import type { VitecConnectionMetadata, VitecOfficeUser } from "../integrations/connections";

export type VitecReach =
  | { readonly kind: "office" }
  | { readonly kind: "realtors"; readonly userIds: readonly string[] };

/**
 * What the customer asks for. `members` is "the realtors this agent's people
 * are", resolved through the connection's `user_links` (the probe matches the
 * agent's members to the office's Express users by e-mail) — the only choice
 * that can be made before the office is active and its realtors are known.
 */
export type VitecReachRequest = VitecReach | { readonly kind: "members" };

/** What one agent may see now. `unapproved`: Squidler has not approved a reach yet. */
export type VitecReachState = VitecReach | { readonly kind: "unapproved" };

/** A new connection's request, matching what Express itself shows a realtor: their own listings. */
export const VITEC_DEFAULT_REACH_REQUEST: VitecReachRequest = { kind: "members" };

export const OFFICE_REACH: VitecReach = { kind: "office" };

/** Express user ids, as Vitec spells them (`HAN…`): compared without regard to case. */
const USER_ID = /^[A-Za-z0-9_-]{1,64}$/;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Distinct, upper-cased, sorted: one spelling per realtor, so two sets compare as values. */
export const normalizeUserIds = (ids: readonly unknown[]): readonly string[] =>
  [...new Set(ids.filter((v): v is string => typeof v === "string" && USER_ID.test(v.trim())).map((v) => v.trim().toUpperCase()))].sort();

/** Total: anything that is not a reach reads as null (not decided). */
export const parseVitecReach = (value: unknown): VitecReach | null => {
  if (!isRecord(value)) return null;
  if (value.kind === "office") return OFFICE_REACH;
  if (value.kind === "realtors" && Array.isArray(value.userIds)) return { kind: "realtors", userIds: normalizeUserIds(value.userIds) };
  return null;
};

export const parseVitecReachRequest = (value: unknown): VitecReachRequest | null =>
  isRecord(value) && value.kind === "members" ? { kind: "members" } : parseVitecReach(value);

/** A request with `members` replaced by the realtors it names today. */
export const resolveVitecReachRequest = (
  request: VitecReachRequest,
  linkedRealtors: readonly string[],
): VitecReach => (request.kind === "members" ? { kind: "realtors", userIds: normalizeUserIds(linkedRealtors) } : request);

/** office ∧ x = x; set ∧ set = the realtors in both. */
export const narrowerReach = (a: VitecReach, b: VitecReach): VitecReach => {
  if (a.kind === "office") return b;
  if (b.kind === "office") return a;
  const inB = new Set(b.userIds);
  return { kind: "realtors", userIds: a.userIds.filter((id) => inB.has(id)) };
};

/** The same reach, or the same request. Ids are normalized on parse, so their order is stable. */
export const sameReach = (a: VitecReachRequest | null, b: VitecReachRequest | null): boolean =>
  a === null || b === null ? a === b
    : a.kind === b.kind && (a.kind !== "realtors" || (b.kind === "realtors" && a.userIds.join(",") === b.userIds.join(",")));

/** What the agent may see: the request (resolved) capped by the approval. */
export const effectiveVitecReach = (requested: VitecReach, approved: VitecReach | null): VitecReachState =>
  approved === null ? { kind: "unapproved" } : narrowerReach(requested, approved);

/**
 * What the request asks for beyond the approval, for "waiting for Squidler":
 * null when nothing waits. The whole office waits as a whole; a set waits
 * realtor by realtor.
 */
export const pendingVitecReach = (requested: VitecReach, approved: VitecReach | null): VitecReach | null => {
  if (approved === null) return requested;
  if (approved.kind === "office") return null;
  if (requested.kind === "office") return OFFICE_REACH;
  const approvedIds = new Set(approved.userIds);
  const waiting = requested.userIds.filter((id) => !approvedIds.has(id));
  return waiting.length === 0 ? null : { kind: "realtors", userIds: waiting };
};

/** Everything a reader shows or enforces about one connection's reach. */
export interface VitecReachView {
  /** What the customer asked for; a request never made reads as the default. */
  readonly requested: VitecReachRequest;
  /** The request with `members` resolved. */
  readonly resolved: VitecReach;
  readonly approved: VitecReach | null;
  readonly effective: VitecReachState;
  /** Asked for beyond the approval: waiting for Squidler. */
  readonly pending: VitecReach | null;
}

/**
 * The one derivation, from the stored halves: the parsed metadata (its
 * `requested_reach`, and its `user_links` — every link, by e-mail or by hand,
 * is a realtor the agent's people are) and the `approved_reach` column as
 * read. The runtime, the settings pane and the admin all call this, so they
 * cannot disagree.
 */
export const vitecReachView = (
  metadata: Pick<VitecConnectionMetadata, "requested_reach" | "user_links">,
  approvedColumn: unknown,
): VitecReachView => {
  const asked = metadata.requested_reach ?? VITEC_DEFAULT_REACH_REQUEST;
  const resolved = resolveVitecReachRequest(asked, Object.values(metadata.user_links).map((l) => l.vitec_user_id));
  const approved = parseVitecReach(approvedColumn);
  return {
    requested: asked,
    resolved,
    approved,
    effective: effectiveVitecReach(resolved, approved),
    pending: pendingVitecReach(resolved, approved),
  };
};

/** A realtor's name as the probe last listed the office's users; the id when unknown. */
export const vitecRealtorName = (users: readonly VitecOfficeUser[], userId: string): string =>
  users.find((u) => u.vitec_user_id.toUpperCase() === userId.toUpperCase())?.name ?? userId;
